import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage
} from '@react-pdf/renderer';
import { getCurrentDateTime } from '../../../../../utils';

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 7
  },
  heading: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 15,
    fontWeight: 'bold'
  },
  subHeading: {
    fontSize: 10,
    textAlign: 'center',
    marginBottom: 10,
    color: '#666'
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: '#1976D2',
    minHeight: 35,
    alignItems: 'center',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#000',
    marginTop: 10
  },
  dataRow: {
    flexDirection: 'row',
    minHeight: 30,
    alignItems: 'center',
    borderBottomWidth: 0.5,
    borderColor: '#ccc',
  },
  // Column width definitions - total should be 100%
  colSrNo: { width: '2.5%', padding: 2 },
  colAdmDate: { width: '4.5%', padding: 2 },
  colAdmTime: { width: '3%', padding: 2 },
  colDisDate: { width: '4.5%', padding: 2 },
  colDisTime: { width: '3%', padding: 2 },
  colUhid: { width: '5%', padding: 2 },
  colIpdNo: { width: '5%', padding: 2 },
  colPatientName: { width: '7%', padding: 2 },
  colAge: { width: '2%', padding: 2 },
  colGender: { width: '2.5%', padding: 2 },
  colGuardian: { width: '6%', padding: 2 },
  colSponsor: { width: '5%', padding: 2 },
  colDepartment: { width: '5%', padding: 2 },
  colDoctor: { width: '6%', padding: 2 },
  colWard: { width: '4%', padding: 2 },
  colRoom: { width: '3%', padding: 2 },
  colBed: { width: '2.5%', padding: 2 },
  colDisMode: { width: '4%', padding: 2 },
  colHealthCond: { width: '4%', padding: 2 },
  colAddress: { width: '7%', padding: 2 },
  colMobile: { width: '4%', padding: 2 },
  colRemarks: { width: '5%', padding: 2 },
  
  // Header text style
  headerText: {
    color: 'white',
    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 2,
    fontFamily: 'Helvetica-Bold'
  },
  // Cell text style
  cellText: {
    fontSize: 5.5,
    padding: 2,
    textAlign: 'left',
  },
  cellTextCenter: {
    fontSize: 5.5,
    padding: 2,
    textAlign: 'center',
  },
  cellTextRight: {
    fontSize: 5.5,
    padding: 2,
    textAlign: 'right',
  },
  logo: {
    width: '100%',
    height: 60,
    marginBottom: 5,
    objectFit: 'contain',
  },
  dateTimeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    fontSize: 8,
    color: '#444'
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    textAlign: 'center',
    fontSize: 7,
    color: '#666'
  }
});

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  }
};

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} size="A4" orientation="landscape" {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={style} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, children, content, ...props }) => (
  <PDFText style={style} {...props}>
    {content || children}
  </PDFText>
);

const COLORS = ['#f9f9f9', '#efefef'];

export const IpdPatientDischargeReportPdf = ({ data }) => {
  const calculateAge = (birthDateString) => {
    if (!birthDateString) return 'N/A';
    const birthDate = new Date(birthDateString);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();

    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }

    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // HH:MM format
  };

  const formatDateTime = (dateString, timeString) => {
    if (!dateString) return '';
    return `${formatDate(dateString)} ${formatTime(timeString)}`;
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const currentDateTime = getCurrentDateTime();

  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="IPD Patient Discharge Register Report">
          <Page>
            <PDFImage
              src="/logo_Hospital_m.jpeg"
              style={styles.logo}
            />
            
            <Text style={styles.heading}>IPD Patient Discharge Register Report</Text>
            
            {/* <View style={styles.dateTimeRow}>
              <Text>Print Date: {formatDate(currentDateTime.date)}</Text>
              <Text>Print Time: {formatTime(currentDateTime.time)}</Text>
              <Text>Printed By: {user?.user?.fullname || 'N/A'}</Text>
            </View> */}

            {/* Header Row */}
            <View style={styles.headerRow}>
              <Text style={{...styles.headerText, ...styles.colSrNo}}>#</Text>
              <Text style={{...styles.headerText, ...styles.colAdmDate}}>Adm Date</Text>
              <Text style={{...styles.headerText, ...styles.colAdmTime}}>Adm Time</Text>
              <Text style={{...styles.headerText, ...styles.colDisDate}}>Dis Date</Text>
              <Text style={{...styles.headerText, ...styles.colDisTime}}>Dis Time</Text>
              <Text style={{...styles.headerText, ...styles.colUhid}}>UHID</Text>
              <Text style={{...styles.headerText, ...styles.colIpdNo}}>IPD No</Text>
              <Text style={{...styles.headerText, ...styles.colPatientName}}>Patient</Text>
              <Text style={{...styles.headerText, ...styles.colAge}}>Age</Text>
              <Text style={{...styles.headerText, ...styles.colGender}}>Gender</Text>
              <Text style={{...styles.headerText, ...styles.colGuardian}}>Guardian</Text>
              <Text style={{...styles.headerText, ...styles.colSponsor}}>Sponsor</Text>
              <Text style={{...styles.headerText, ...styles.colDepartment}}>Dept</Text>
              <Text style={{...styles.headerText, ...styles.colDoctor}}>Doctor</Text>
              <Text style={{...styles.headerText, ...styles.colWard}}>Ward</Text>
              <Text style={{...styles.headerText, ...styles.colRoom}}>Room</Text>
              <Text style={{...styles.headerText, ...styles.colBed}}>Bed</Text>
              <Text style={{...styles.headerText, ...styles.colDisMode}}>Dis Mode</Text>
              <Text style={{...styles.headerText, ...styles.colHealthCond}}>Health</Text>
              <Text style={{...styles.headerText, ...styles.colAddress}}>Address</Text>
              <Text style={{...styles.headerText, ...styles.colMobile}}>Mobile</Text>
              <Text style={{...styles.headerText, ...styles.colRemarks}}>Remarks</Text>
            </View>
            
            {/* Data Rows */}
            {data && data.length > 0 ? (
              data.map((item, index) => (
                <View key={index} style={{...styles.dataRow, backgroundColor: COLORS[index % COLORS.length]}}>
                  <Text style={{...styles.cellTextCenter, ...styles.colSrNo}}>{index + 1}</Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colAdmDate}}>{formatDate(item?.admission_date)}</Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colAdmTime}}>{formatTime(item?.admission_time)}</Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colDisDate}}>{formatDate(item?.discharge_date)}</Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colDisTime}}>{formatTime(item?.discharge_time)}</Text>
                  <Text style={{...styles.cellText, ...styles.colUhid}}>{item?.uhid || ''}</Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colIpdNo}}>{item?.ipd_no || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colPatientName}}>
                    {`${item.patient?.name_suffix || ''} ${item.patient?.first_name || ''} ${item.patient?.last_name || ''}`.trim()}
                  </Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colAge}}>{calculateAge(item.patient?.dob)}</Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colGender}}>{item.patient?.gender || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colGuardian}}>{item.patient?.guardian_name || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colSponsor}}>{item.patient?.sponsor || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colDepartment}}>{item.consultant_detail?.department || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colDoctor}}>{item.consultant_detail?.doctor || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colWard}}>{item.consultant_detail?.ward || ''}</Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colRoom}}>{item.consultant_detail?.room_no || ''}</Text>
                  <Text style={{...styles.cellTextCenter, ...styles.colBed}}>{item.consultant_detail?.bed_no || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colDisMode}}>{item?.discharge_mode || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colHealthCond}}>{item?.health_condition || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colAddress}}>{item.patient?.address || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colMobile}}>{item.patient?.mobile || ''}</Text>
                  <Text style={{...styles.cellText, ...styles.colRemarks}}>{item?.remarks || ''}</Text>
                </View>
              ))
            ) : (
              <View style={styles.dataRow}>
                <Text style={{...styles.cellText, width: '100%', textAlign: 'center', padding: 20}}>
                  No data available
                </Text>
              </View>
            )}
            
            {/* Footer with summary */}
            <View style={styles.footer}>
              <Text>Total Records: {data?.length || 0}</Text>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    </div>
  );
};